// src/utils/editionCommentaire.ts — SCE Issues
// Corriger son propre commentaire (23/10, demande de Guillaume) : chacun peut
// reprendre le texte de SES commentaires, jamais ceux des autres. Pur : la fiche
// s'en sert pour montrer le crayon, l'API pour refuser d'écrire.
//
// Règles :
//  - un COMMENTAIRE seulement : les événements écrits par l'appli
//    (qualification, clôture, modification…) restent tels quels ;
//  - par son auteur : son adresse, ramenée à la personne (compte secondaire,
//    appareil partagé), parmi les identités de qui est connecté ;
//  - là où l'on peut commenter (`commentairesOuverts`) : en cours ou clos
//    (26/10, Guillaume : on revient sur un clos après coup), jamais un annulé.
//
// Pas de colonne pour dire « modifié » : la date de dernière modification de
// la ligne SharePoint suffit. La création écrit deux fois (le texte, puis son
// N° C03 un instant après) : en deçà de DELAI_CREATION_MS, ce n'est pas une
// correction.

import { ANNULE, type Commentaire, type Statut } from '@/types/issue.types'
import { estDeclarePar } from './identiteDeclarant'

export const DELAI_CREATION_MS = 2 * 60 * 1000

/** Peut-on commenter ce problème ? Oui, clos compris (26/10) ; pas un annulé. */
export const commentairesOuverts = (statut: Statut): boolean => statut !== ANNULE

/** Date de la correction, ou undefined si la ligne n'a pas bougé depuis sa création. */
export function dateDeCorrection(creeLe: string | undefined, modifieLe: string | undefined): string | undefined {
  if (!creeLe || !modifieLe) return undefined
  const ecart = Date.parse(modifieLe) - Date.parse(creeLe)
  return Number.isFinite(ecart) && ecart > DELAI_CREATION_MS ? modifieLe : undefined
}

/**
 * La personne connectée peut-elle corriger ce commentaire ?
 *
 * @param identites ses identités (identitesDeclarant)
 * @param ouverts on peut commenter ce problème (`commentairesOuverts`)
 */
export function peutModifierCommentaire(
  c: Pick<Commentaire, 'type' | 'auteurEmail' | 'itemId'>,
  identites: readonly string[],
  ouverts: boolean,
): boolean {
  return ouverts && c.type === 'Commentaire' && !!c.itemId && estDeclarePar(c.auteurEmail, identites)
}

/** Ce qui empêche d'enregistrer la correction, ou null. */
export function erreurCorrection(avant: string, apres: string): string | null {
  if (apres.trim() === '') return 'Le commentaire ne peut pas être vide.'
  if (apres.trim() === avant.trim()) return 'Rien n’a changé.'
  return null
}

const HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
const JOUR = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' })

/**
 * « modifié à 14:32 » (même jour que le commentaire), sinon « modifié le 24/10
 * à 09:10 ». Affiché en clair à côté de la date : visible au téléphone aussi.
 */
export function libelleCorrection(ecritLe: string, corrigeLe: string): string {
  const d = new Date(corrigeLe)
  if (Number.isNaN(d.getTime())) return 'modifié'
  const memeJour = JOUR.format(d) === JOUR.format(new Date(ecritLe))
  return memeJour ? `modifié à ${HEURE.format(d)}` : `modifié le ${JOUR.format(d)} à ${HEURE.format(d)}`
}

// ─── Historique (23/10) ───
//
// Lu dans l'historique des versions de SharePoint (activé sur SCE_Commentaires),
// au clic sur « modifié à … » : aucune colonne, rien d'écrit. Une version est
// gardée à CHAQUE écriture de la ligne, texte inchangé compris (le N° C03 posé
// juste après la création) : on ne garde que les changements de texte.

/** Une version telle que SharePoint la rend : sa date et le texte à ce moment-là. */
export interface VersionBrute {
  date: string
  contenu?: string
  /** Qui a écrit cette version (« Prénom Nom »), si on le sait. */
  par?: string
}

export interface VersionCommentaire {
  date: string
  contenu: string
  /** Le texte d'origine (le plus ancien). */
  origine: boolean
  /** Qui a écrit ce texte : l'auteur de la version où il est apparu. */
  par?: string
}

/**
 * Les textes successifs du commentaire, le plus récent d'abord. Chaque texte
 * porte la date où il est APPARU (pas celle d'une réécriture sans changement).
 */
export function versionsDuCommentaire(brutes: readonly VersionBrute[]): VersionCommentaire[] {
  const chrono = brutes
    .filter((v) => typeof v.contenu === 'string' && Number.isFinite(Date.parse(v.date)))
    .slice()
    .sort((a, b) => Date.parse(a.date) - Date.parse(b.date))
  const textes: VersionCommentaire[] = []
  for (const v of chrono) {
    const contenu = (v.contenu ?? '').trim()
    if (textes.length > 0 && textes[textes.length - 1]!.contenu === contenu) continue
    textes.push({ date: v.date, contenu, origine: textes.length === 0, ...(v.par ? { par: v.par } : {}) })
  }
  return textes.reverse()
}
